import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, RefreshCw, Calendar, TrendingUp, TrendingDown, AlertTriangle, ArrowRight, FileText, Sun } from "lucide-react";
import axios from "axios";
import { logger } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/sentinel`;

// ─── Sub-components ──────────────────────────────────────────────────────

function ScoreBadge({ score }) {
  const color = score > 0 ? '#22c55e' : score < 0 ? '#ef4444' : '#8b949e';
  return (
    <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded"
      style={{ color, backgroundColor: `${color}15`, border: `1px solid ${color}30` }}>
      {score > 0 ? '+' : ''}{score}
    </span>
  );
}

function SentimentTrajectoryChart({ hourlyAverages }) {
  if (!hourlyAverages || hourlyAverages.length === 0) return null;

  const width = 500;
  const height = 80;
  const padding = { top: 10, bottom: 20, left: 35, right: 10 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const minH = Math.min(...hourlyAverages.map(h => h.avg_score), -1);
  const maxH = Math.max(...hourlyAverages.map(h => h.avg_score), 1);
  const range = Math.max(Math.abs(minH), Math.abs(maxH), 1);

  const points = hourlyAverages.map((h, i) => {
    const x = padding.left + (i / Math.max(hourlyAverages.length - 1, 1)) * chartW;
    const y = padding.top + chartH / 2 - (h.avg_score / range) * (chartH / 2);
    return { x, y, ...h };
  });

  const zeroY = padding.top + chartH / 2;
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxWidth: 500 }}>
      {/* Zero line */}
      <line x1={padding.left} y1={zeroY} x2={width - padding.right} y2={zeroY}
        stroke="#30363d" strokeWidth="1" strokeDasharray="3,3" />
      {/* Y labels */}
      <text x={padding.left - 5} y={padding.top + 4} textAnchor="end"
        fill="#484f58" fontSize="8" fontFamily="monospace">+{range.toFixed(0)}</text>
      <text x={padding.left - 5} y={zeroY + 3} textAnchor="end"
        fill="#484f58" fontSize="8" fontFamily="monospace">0</text>
      <text x={padding.left - 5} y={height - padding.bottom + 4} textAnchor="end"
        fill="#484f58" fontSize="8" fontFamily="monospace">-{range.toFixed(0)}</text>
      {/* Path */}
      <path d={pathD} fill="none" stroke="#ff6b2b" strokeWidth="2" />
      {/* Dots */}
      {points.map((p, i) => (
        <circle key={`point-${p.hour}-${i}`} cx={p.x} cy={p.y} r="3"
          fill={p.avg_score > 0 ? '#22c55e' : p.avg_score < 0 ? '#ef4444' : '#8b949e'}
          stroke="#0d1117" strokeWidth="1" />
      ))}
      {/* X labels */}
      {points.filter((_, i) => i % Math.max(1, Math.floor(points.length / 5)) === 0 || i === points.length - 1).map((p, i) => (
        <text key={`label-${p.hour}-${i}`} x={p.x} y={height - 3} textAnchor="middle"
          fill="#484f58" fontSize="8" fontFamily="monospace">{p.hour}:00</text>
      ))}
    </svg>
  );
}

function ScoreDistribution({ distribution }) {
  if (!distribution) return null;
  const maxCount = Math.max(...Object.values(distribution), 1);
  return (
    <div className="flex items-end gap-0.5 h-8">
      {Array.from({ length: 11 }, (_, i) => i - 5).map(score => {
        const count = distribution[String(score)] || 0;
        const h = count > 0 ? Math.max(4, (count / maxCount) * 100) : 0;
        const color = score > 0 ? '#22c55e' : score < 0 ? '#ef4444' : '#8b949e';
        return (
          <div key={score} className="flex flex-col items-center gap-0.5 flex-1">
            <div style={{ height: `${h}%`, backgroundColor: color, minWidth: 6, borderRadius: 1, width: '100%', opacity: 0.7 }} />
            <span className="font-mono" style={{ fontSize: 7, color: '#484f58' }}>{score}</span>
          </div>
        );
      })}
    </div>
  );
}

function Card({ title, children, icon }) {
  return (
    <div className="mb-4 rounded-lg overflow-hidden" style={{ backgroundColor: '#161b22', border: '1px solid #21262d' }}>
      {title && (
        <div className="px-4 py-2 border-b flex items-center gap-2" style={{ borderColor: '#21262d' }}>
          {icon}
          <h4 className="font-mono text-[11px] font-bold tracking-wider" style={{ color: '#8b949e' }}>{title}</h4>
        </div>
      )}
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}


// ─── Main Component ─────────────────────────────────────────────────────

export default function RecapPanel({ isOpen, onClose }) {
  const [recap, setRecap] = useState(null);
  const [morningBrief, setMorningBrief] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState("daily_recap");
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/recap/history?limit=30`);
      setHistory(res.data.recaps || []);
    } catch (err) {
      logger.warn("[RecapPanel] History fetch:", err.message);
    }
  }, []);

  const fetchDate = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    setRecap(null);
    setMorningBrief(null);
    setSelectedDate(date);

    try {
      const [recapRes, morningRes] = await Promise.allSettled([
        axios.get(`${API}/recap/${date}`),
        axios.get(`${API}/recap/${date}/morning`)
      ]);
      if (recapRes.status === "fulfilled") setRecap(recapRes.value.data);
      if (morningRes.status === "fulfilled") setMorningBrief(morningRes.value.data);
    } catch (err) {
      setError("Failed to load recap data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/recap/latest`);
      if (res.data.status === "none") {
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
        setSelectedDate(today);
        setRecap(null);
      } else {
        setRecap(res.data);
        setSelectedDate(res.data.date);
        // Also try to fetch morning brief for same date
        try {
          const mRes = await axios.get(`${API}/recap/${res.data.date}/morning`);
          setMorningBrief(mRes.data);
        } catch (err) {
          logger.debug("[RecapPanel] No morning brief for date:", err.message);
        }
      }
    } catch (err) {
      setError("Failed to load recap");
      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      setSelectedDate(today);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchLatest();
      fetchHistory();
      setActiveTab("daily_recap");
    }
  }, [isOpen, fetchLatest, fetchHistory]);

  // Escape key to close
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape" && isOpen) onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleGenerate = async (type = "daily_recap", force = false) => {
    setGenerating(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/recap/generate`, { date: selectedDate, type, force });
      if (type === "morning_brief") {
        setMorningBrief(res.data);
        setActiveTab("morning_brief");
      } else {
        setRecap(res.data);
        setActiveTab("daily_recap");
      }
      fetchHistory();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to generate ${type}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    await handleGenerate(activeTab, true);
  };

  const navigateDate = (dir) => {
    if (!selectedDate) return;
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + dir);
    fetchDate(d.toISOString().split('T')[0]);
  };

  if (!isOpen) return null;

  const narrative = recap?.narrative || {};
  const stats = recap?.stats || {};
  const hasMorningBrief = !!morningBrief?.narrative;
  const hasRecap = recap?.status === "generated";
  const mNarrative = morningBrief?.narrative || {};

  return (
    <div data-testid="recap-overlay"
      className="fixed inset-0 z-50"
      style={{ backgroundColor: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(8px)', animation: 'fadeIn 200ms ease' }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="h-full flex flex-col" style={{ backgroundColor: '#0d1117' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b shrink-0"
          style={{ borderColor: '#21262d', backgroundColor: '#161b22' }}>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-bold tracking-wide" style={{ color: '#e6edf3' }}>
              {activeTab === "daily_recap" ? "DAILY RECAP" : "MORNING BRIEF"}
            </span>
            <span className="font-mono text-xs" style={{ color: '#484f58' }}>—</span>
            <div className="flex items-center gap-1">
              <button data-testid="recap-prev-date" onClick={() => navigateDate(-1)}
                className="p-1 rounded hover:bg-white/5 transition-colors" style={{ color: '#8b949e' }}>
                <ChevronLeft size={16} />
              </button>
              <span className="font-mono text-sm font-bold px-1" style={{ color: '#e6edf3' }}>
                {selectedDate || '—'}
              </span>
              <button data-testid="recap-next-date" onClick={() => navigateDate(1)}
                className="p-1 rounded hover:bg-white/5 transition-colors" style={{ color: '#8b949e' }}>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Tab toggle */}
            {(hasRecap || hasMorningBrief) && (
              <div className="flex rounded overflow-hidden" style={{ border: '1px solid #30363d' }}>
                <button data-testid="tab-daily-recap"
                  onClick={() => setActiveTab("daily_recap")}
                  className="px-2 py-1 font-mono text-[10px] transition-colors"
                  style={{
                    color: activeTab === "daily_recap" ? '#e6edf3' : '#484f58',
                    backgroundColor: activeTab === "daily_recap" ? '#30363d' : 'transparent'
                  }}>
                  <FileText size={11} className="inline mr-1" style={{ verticalAlign: -1 }} />RECAP
                </button>
                <button data-testid="tab-morning-brief"
                  onClick={() => setActiveTab("morning_brief")}
                  className="px-2 py-1 font-mono text-[10px] transition-colors"
                  style={{
                    color: activeTab === "morning_brief" ? '#e6edf3' : '#484f58',
                    backgroundColor: activeTab === "morning_brief" ? '#30363d' : 'transparent'
                  }}>
                  <Sun size={11} className="inline mr-1" style={{ verticalAlign: -1 }} />MORNING
                </button>
              </div>
            )}
            <button data-testid="recap-history-toggle"
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-1 px-2 py-1 rounded font-mono text-[10px] transition-colors"
              style={{
                color: showHistory ? '#ff6b2b' : '#8b949e',
                border: `1px solid ${showHistory ? 'rgba(255,107,43,0.3)' : '#30363d'}`,
              }}>
              <Calendar size={11} />HISTORY
            </button>
            <button data-testid="recap-close" onClick={onClose}
              className="p-1 rounded hover:bg-white/5 transition-colors" style={{ color: '#8b949e' }}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* History sidebar */}
          {showHistory && (
            <div className="w-44 shrink-0 border-r overflow-y-auto"
              style={{ borderColor: '#21262d', backgroundColor: '#0d1117' }}>
              <div className="p-2">
                <p className="font-mono text-[9px] mb-2 px-1 tracking-wider" style={{ color: '#484f58' }}>PAST RECAPS</p>
                {history.length === 0 && (
                  <p className="font-mono text-[10px] px-1" style={{ color: '#484f58' }}>No recaps yet</p>
                )}
                {history.filter(h => h.type === "daily_recap").map(h => (
                  <button key={h.date} onClick={() => fetchDate(h.date)}
                    className="w-full text-left px-2 py-1.5 rounded font-mono text-[11px] transition-colors mb-0.5"
                    style={{
                      color: h.date === selectedDate ? '#e6edf3' : '#8b949e',
                      backgroundColor: h.date === selectedDate ? 'rgba(255,107,43,0.08)' : 'transparent',
                    }}>
                    <div>{h.date}</div>
                    <div className="flex items-center gap-2" style={{ color: '#484f58', fontSize: 9 }}>
                      <span>{h.stats?.total_posts || 0} posts</span>
                      <span>avg {h.stats?.avg_score >= 0 ? '+' : ''}{h.stats?.avg_score || 0}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Main scrollable content */}
          <div className="flex-1 overflow-y-auto p-5">
            {/* Loading */}
            {(loading || generating) && (
              <div className="flex flex-col items-center justify-center py-20">
                <div style={{ width: 32, height: 32, border: '2px solid #30363d', borderTopColor: '#ff6b2b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <p className="font-mono text-xs mt-4" style={{ color: '#8b949e' }}>
                  {generating ? 'Generating analysis...' : 'Loading...'}
                </p>
              </div>
            )}

            {error && !loading && !generating && (
              <div className="text-center py-10">
                <p className="font-mono text-sm" style={{ color: '#ef4444' }}>{error}</p>
              </div>
            )}

            {/* ── DAILY RECAP TAB ── */}
            {!loading && !generating && !error && activeTab === "daily_recap" && (
              <>
                {!hasRecap ? (
                  <EmptyState
                    date={selectedDate}
                    type="daily_recap"
                    onGenerate={() => handleGenerate("daily_recap")}
                  />
                ) : (
                  <div className="max-w-3xl mx-auto">
                    {/* MARKET MOOD */}
                    <Card title="MARKET MOOD">
                      <p className="font-mono text-sm leading-relaxed" style={{ color: '#e6edf3' }}>
                        {narrative.market_mood}
                      </p>
                    </Card>

                    {/* DAY IN REVIEW */}
                    <Card title="DAY IN REVIEW">
                      <p className="font-mono text-xs leading-relaxed whitespace-pre-line" style={{ color: '#c9d1d9' }}>
                        {narrative.narrative}
                      </p>
                    </Card>

                    {/* SESSION STATS */}
                    <Card title="SESSION STATS">
                      <div className="flex items-center gap-6 mb-4">
                        <Stat label="POSTS" value={stats.total_posts} />
                        <Stat label="AVG" value={`${stats.avg_score >= 0 ? '+' : ''}${stats.avg_score}`}
                          color={stats.avg_score > 0 ? '#22c55e' : stats.avg_score < 0 ? '#ef4444' : '#8b949e'} />
                        <Stat label="EXTREME EVENTS" value={stats.extreme_event_count} color="#f59e0b" />
                      </div>
                      {/* Sentiment trajectory chart */}
                      {stats.hourly_averages?.length > 0 && (
                        <div className="mb-3">
                          <p className="font-mono text-[9px] mb-1 tracking-wider" style={{ color: '#484f58' }}>SENTIMENT TRAJECTORY</p>
                          <SentimentTrajectoryChart hourlyAverages={stats.hourly_averages} />
                          {narrative.sentiment_trajectory && (
                            <p className="font-mono text-[11px] mt-2 leading-relaxed" style={{ color: '#8b949e' }}>
                              {narrative.sentiment_trajectory}
                            </p>
                          )}
                        </div>
                      )}
                      {/* Score distribution */}
                      {stats.distribution && (
                        <div>
                          <p className="font-mono text-[9px] mb-1 tracking-wider" style={{ color: '#484f58' }}>SCORE DISTRIBUTION</p>
                          <ScoreDistribution distribution={stats.distribution} />
                        </div>
                      )}
                    </Card>

                    {/* TOP 5 EVENTS */}
                    {recap.top_events?.length > 0 && (
                      <Card title="TOP 5 EVENTS" icon={<TrendingUp size={13} style={{ color: '#ff6b2b' }} />}>
                        <div className="space-y-3">
                          {recap.top_events.map((ev, i) => {
                            const cleanHandle = (ev.source_handle || '').replace(/^@/, '');
                            const cleanPostId = (ev.post_id || '').replace(/^tw-/, '');
                            const tweetUrl = cleanPostId && cleanHandle
                              ? `https://x.com/${cleanHandle}/status/${cleanPostId}`
                              : null;
                            const profileUrl = cleanHandle
                              ? `https://x.com/${cleanHandle}`
                              : null;

                            return (
                              <div key={`event-${i}`} className="pb-3 border-b last:border-0" style={{ borderColor: '#21262d' }}>
                                <div className="flex items-center gap-2 mb-1">
                                  <ScoreBadge score={ev.overall_score} />
                                  {profileUrl ? (
                                    <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                                      className="font-mono text-[11px] font-bold transition-opacity hover:opacity-70"
                                      style={{ color: '#e6edf3', textDecoration: 'none' }}
                                      data-testid={`recap-event-handle-${i}`}>
                                      @{cleanHandle}
                                    </a>
                                  ) : (
                                    <span className="font-mono text-[11px] font-bold" style={{ color: '#e6edf3' }}>
                                      @{cleanHandle}
                                    </span>
                                  )}
                                  <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>
                                    {formatTime(ev.timestamp)}
                                  </span>
                                  {tweetUrl && (
                                    <a href={tweetUrl} target="_blank" rel="noopener noreferrer"
                                      className="font-mono text-[9px] px-1.5 py-0.5 rounded transition-colors hover:bg-white/10"
                                      style={{ color: '#484f58', border: '1px solid #30363d', textDecoration: 'none' }}
                                      data-testid={`recap-event-link-${i}`}>
                                      VIEW
                                    </a>
                                  )}
                                </div>
                                {tweetUrl ? (
                                  <a href={tweetUrl} target="_blank" rel="noopener noreferrer"
                                    className="block font-mono text-xs mb-1 transition-opacity hover:opacity-70"
                                    style={{ color: '#c9d1d9', textDecoration: 'none' }}
                                    data-testid={`recap-event-text-${i}`}>
                                    {ev.text}
                                  </a>
                                ) : (
                                  <p className="font-mono text-xs mb-1" style={{ color: '#c9d1d9' }}>{ev.text}</p>
                                )}
                                <div className="flex flex-wrap gap-1 mb-1">
                                  {ev.instruments?.map((inst, j) => (
                                    <span key={`inst-${inst.symbol}-${j}`} className="font-mono text-[10px] px-1 py-0.5 rounded"
                                      style={{
                                        color: inst.score > 0 ? '#22c55e' : inst.score < 0 ? '#ef4444' : '#8b949e',
                                        backgroundColor: inst.score > 0 ? 'rgba(34,197,94,0.1)' : inst.score < 0 ? 'rgba(239,68,68,0.1)' : 'rgba(139,148,158,0.1)',
                                      }}>
                                      {inst.symbol} {inst.score > 0 ? '+' : ''}{inst.score}
                                    </span>
                                  ))}
                                </div>
                                {narrative.top_events_analysis?.[i] && (
                                  <p className="font-mono text-[11px]" style={{ color: '#8b949e' }}>
                                    <ArrowRight size={10} className="inline mr-1" style={{ verticalAlign: -1 }} />
                                    {narrative.top_events_analysis[i]}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    )}

                    {/* INSTRUMENT LEADERBOARD */}
                    {recap.instrument_leaderboard && Object.keys(recap.instrument_leaderboard).length > 0 && (
                      <Card title="INSTRUMENT LEADERBOARD">
                        <div className="space-y-1.5 mb-3">
                          {Object.entries(recap.instrument_leaderboard).map(([sym, data]) => {
                            const dir = data.net_score > 0;
                            const Icon = dir ? TrendingUp : TrendingDown;
                            const color = dir ? '#22c55e' : '#ef4444';
                            return (
                              <div key={sym} className="flex items-center gap-3">
                                <Icon size={12} style={{ color }} />
                                <span className="font-mono text-xs font-bold w-16" style={{ color: '#e6edf3' }}>{sym}</span>
                                <span className="font-mono text-xs font-bold" style={{ color }}>
                                  {data.net_score > 0 ? '+' : ''}{data.net_score}
                                </span>
                                <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>
                                  ({data.mentions} mentions, avg {data.avg_score > 0 ? '+' : ''}{data.avg_score})
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {narrative.instrument_analysis && (
                          <p className="font-mono text-[11px] leading-relaxed pt-2 border-t" style={{ color: '#8b949e', borderColor: '#21262d' }}>
                            {narrative.instrument_analysis}
                          </p>
                        )}
                      </Card>
                    )}

                    {/* ALERTS FIRED */}
                    {recap.alerts_fired?.length > 0 && (
                      <Card title={`ALERTS FIRED (${recap.alerts_fired.length})`} icon={<AlertTriangle size={13} style={{ color: '#f59e0b' }} />}>
                        <div className="space-y-1.5">
                          {recap.alerts_fired.map((a, i) => {
                            const aCleanHandle = (a.source_handle || '').replace(/^@/, '');
                            const aCleanPostId = (a.post_id || '').replace(/^tw-/, '');
                            const alertTweetUrl = aCleanPostId && aCleanHandle
                              ? `https://x.com/${aCleanHandle}/status/${aCleanPostId}`
                              : null;
                            return (
                              <div key={`alert-${i}`} className="flex items-start gap-2">
                                <ScoreBadge score={a.overall_score} />
                                {alertTweetUrl ? (
                                  <a href={alertTweetUrl} target="_blank" rel="noopener noreferrer"
                                    className="font-mono text-[11px] font-bold shrink-0 transition-opacity hover:opacity-70"
                                    style={{ color: '#e6edf3', textDecoration: 'none' }}>
                                    @{aCleanHandle}
                                  </a>
                                ) : (
                                  <span className="font-mono text-[11px] font-bold shrink-0" style={{ color: '#e6edf3' }}>@{aCleanHandle}</span>
                                )}
                                <span className="font-mono text-[10px] shrink-0" style={{ color: '#484f58' }}>{formatTime(a.timestamp)}</span>
                                {alertTweetUrl ? (
                                  <a href={alertTweetUrl} target="_blank" rel="noopener noreferrer"
                                    className="font-mono text-[11px] truncate transition-opacity hover:opacity-70"
                                    style={{ color: '#8b949e', textDecoration: 'none' }}>
                                    — {a.text}
                                  </a>
                                ) : (
                                  <span className="font-mono text-[11px] truncate" style={{ color: '#8b949e' }}>— {a.text}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    )}

                    {/* CARRY FORWARD */}
                    {narrative.carry_forward?.length > 0 && (
                      <Card title="CARRY FORWARD — Watch Tomorrow" icon={<ArrowRight size={13} style={{ color: '#3b82f6' }} />}>
                        <div className="space-y-2">
                          {narrative.carry_forward.map((item, i) => (
                            <div key={`cf-${i}`} className="flex items-start gap-2">
                              <ArrowRight size={11} className="shrink-0 mt-0.5" style={{ color: '#3b82f6' }} />
                              <p className="font-mono text-[11px] leading-relaxed" style={{ color: '#c9d1d9' }}>{item}</p>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* RISK FLAGS */}
                    {narrative.risk_flags?.length > 0 && (
                      <Card title="RISK FLAGS" icon={<AlertTriangle size={13} style={{ color: '#ef4444' }} />}>
                        <div className="space-y-2">
                          {narrative.risk_flags.map((item, i) => (
                            <div key={`rf-${i}`} className="flex items-start gap-2">
                              <ArrowRight size={11} className="shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
                              <p className="font-mono text-[11px] leading-relaxed" style={{ color: '#c9d1d9' }}>{item}</p>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: '#21262d' }}>
                      <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>
                        Generated {recap.generated_at ? new Date(recap.generated_at).toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true }) : ''} ET
                        {recap.model ? ` · ${recap.model}` : ''}
                        {recap.latency_ms ? ` · ${(recap.latency_ms / 1000).toFixed(1)}s` : ''}
                      </span>
                      <button data-testid="recap-regenerate-btn" onClick={handleRegenerate} disabled={generating}
                        className="flex items-center gap-1.5 px-2 py-1 rounded font-mono text-[10px] transition-colors hover:bg-white/5"
                        style={{ color: '#484f58', border: '1px solid #30363d' }}>
                        <RefreshCw size={10} />REGENERATE
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── MORNING BRIEF TAB ── */}
            {!loading && !generating && !error && activeTab === "morning_brief" && (
              <>
                {!hasMorningBrief ? (
                  <EmptyState
                    date={selectedDate}
                    type="morning_brief"
                    onGenerate={() => handleGenerate("morning_brief")}
                  />
                ) : (
                  <div className="max-w-3xl mx-auto">
                    {/* OPENING STANCE */}
                    <Card title="OPENING STANCE" icon={<Sun size={13} style={{ color: '#f59e0b' }} />}>
                      <p className="font-mono text-sm leading-relaxed" style={{ color: '#e6edf3' }}>
                        {mNarrative.opening_stance}
                      </p>
                    </Card>

                    {/* OVERNIGHT SUMMARY */}
                    <Card title="OVERNIGHT SUMMARY">
                      <p className="font-mono text-xs leading-relaxed" style={{ color: '#c9d1d9' }}>
                        {mNarrative.overnight_summary}
                      </p>
                      {morningBrief.overnight_extreme_events?.length > 0 && (
                        <div className="mt-3 pt-2 border-t" style={{ borderColor: '#21262d' }}>
                          <p className="font-mono text-[9px] mb-1 tracking-wider" style={{ color: '#484f58' }}>EXTREME EVENTS</p>
                          {morningBrief.overnight_extreme_events.map((ev, i) => (
                            <div key={`oee-${i}`} className="flex items-center gap-2 mb-1">
                              <ScoreBadge score={ev.overall_score} />
                              <span className="font-mono text-[11px]" style={{ color: '#8b949e' }}>
                                @{ev.source_handle} — {ev.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>

                    {/* YESTERDAY'S CARRY-FORWARD STATUS */}
                    {mNarrative.carry_forward_status?.length > 0 && (
                      <Card title="YESTERDAY'S CARRY-FORWARD STATUS">
                        <div className="space-y-2">
                          {mNarrative.carry_forward_status.map((item, i) => (
                            <div key={`cfs-${i}`} className="flex items-start gap-2">
                              <ArrowRight size={11} className="shrink-0 mt-0.5" style={{ color: '#3b82f6' }} />
                              <p className="font-mono text-[11px] leading-relaxed" style={{ color: '#c9d1d9' }}>{item}</p>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* TODAY'S WATCHLIST */}
                    {mNarrative.todays_watchlist?.length > 0 && (
                      <Card title="TODAY'S WATCHLIST">
                        <div className="space-y-2">
                          {mNarrative.todays_watchlist.map((item, i) => (
                            <div key={`tw-${i}`} className="flex items-start gap-2">
                              <ArrowRight size={11} className="shrink-0 mt-0.5" style={{ color: '#f59e0b' }} />
                              <p className="font-mono text-[11px] leading-relaxed" style={{ color: '#c9d1d9' }}>{item}</p>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: '#21262d' }}>
                      <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>
                        Generated {morningBrief.generated_at ? new Date(morningBrief.generated_at).toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true }) : ''} ET
                        {morningBrief.model ? ` · ${morningBrief.model}` : ''}
                      </span>
                      <button data-testid="recap-regenerate-btn" onClick={handleRegenerate} disabled={generating}
                        className="flex items-center gap-1.5 px-2 py-1 rounded font-mono text-[10px] transition-colors hover:bg-white/5"
                        style={{ color: '#484f58', border: '1px solid #30363d' }}>
                        <RefreshCw size={10} />REGENERATE
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Small helpers ──────────────────────────────────────────────────────

function Stat({ label, value, color = '#e6edf3' }) {
  return (
    <div className="text-center">
      <p className="font-mono text-sm font-bold" style={{ color }}>{value ?? '—'}</p>
      <p className="font-mono text-[9px] tracking-wider" style={{ color: '#484f58' }}>{label}</p>
    </div>
  );
}

function EmptyState({ date, type, onGenerate }) {
  const isRecap = type === "daily_recap";
  return (
    <div className="text-center py-20">
      {isRecap ? <FileText size={40} style={{ color: '#30363d', margin: '0 auto 16px' }} /> : <Sun size={40} style={{ color: '#30363d', margin: '0 auto 16px' }} />}
      <p className="font-mono text-sm mb-1" style={{ color: '#8b949e' }}>
        No {isRecap ? 'daily recap' : 'morning brief'} for {date}
      </p>
      <p className="font-mono text-[11px] mb-6" style={{ color: '#484f58' }}>
        {isRecap ? "Generate a recap from the day's analyzed posts" : "Generate a morning brief with overnight activity and yesterday's carry-forward"}
      </p>
      <button data-testid="recap-generate-btn" onClick={onGenerate}
        className="px-4 py-2 rounded font-mono text-xs font-medium transition-all hover:brightness-110"
        style={{ color: '#ff6b2b', border: '1px solid rgba(255,107,43,0.3)', backgroundColor: 'rgba(255,107,43,0.08)' }}>
        GENERATE {isRecap ? 'RECAP' : 'MORNING BRIEF'}
      </button>
    </div>
  );
}

function formatTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }) + ' ET';
  } catch { return ts; }
}
